import { Box, Button, Card, Collapsible, Text } from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'

import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type { PbxFormValues, SelectOption } from '../types/pbxSettings.types'
import {
  ADVANCED_NUMERIC_FIELDS,
  INDUSTRY_OPTIONS,
} from '../utils/pbxSettings.constants'
import { getVisibleEhrOptions } from '../utils/pbxSettings.utils'
import { InputField, SelectField } from './PbxFormFields'

interface PbxSettingsAdvancedSectionProps {
  t?: PbxSettingsTranslateFn
  pbx: PbxFormValues
  serverGroupOptions: SelectOption[]
  isInternalUser: boolean
  onPbxFieldChange: <K extends keyof PbxFormValues>(
    field: K,
    value: PbxFormValues[K],
  ) => void
}

export const PbxSettingsAdvancedSection = ({
  t,
  pbx,
  serverGroupOptions,
  isInternalUser,
  onPbxFieldChange,
}: PbxSettingsAdvancedSectionProps) => {
  const ehrOptions = getVisibleEhrOptions(isInternalUser, pbx.ehr_type)

  return (
    <Collapsible.Root>
      <Card.Root bg="bg.panel" rounded="xl">
        <Card.Header p={2}>
          <Collapsible.Trigger asChild>
            <Button variant="ghost" justifyContent="space-between" width="full">
              <Box as="span">
                {text(
                  t,
                  'pbxSettings.sections.advanced.title',
                  'Advanced PBX Settings',
                )}{' '}
                <Text as="span" color="fg.error">
                  *
                </Text>
              </Box>
              <Collapsible.Indicator
                transition="transform 0.2s"
                _open={{ transform: 'rotate(-180deg)' }}
              >
                <ChevronDown size={16} />
              </Collapsible.Indicator>
            </Button>
          </Collapsible.Trigger>
        </Card.Header>
        <Collapsible.Content>
          <Card.Body p={6} pt={0}>
            <Box display="grid" gridTemplateColumns="1fr" gap={4}>
              <SelectField
                t={t}
                label={text(
                  t,
                  'pbxSettings.fields.serverGroup.label',
                  'Server Group',
                )}
                required
                value={pbx.server_group_id}
                onChange={(value) => onPbxFieldChange('server_group_id', value)}
                options={serverGroupOptions}
                placeholder={text(
                  t,
                  'pbxSettings.placeholders.selectServerGroup',
                  'Select server group',
                )}
              />

              {ADVANCED_NUMERIC_FIELDS.map((fieldConfig) => (
                <InputField
                  key={fieldConfig.key}
                  t={t}
                  label={text(
                    t,
                    `pbxSettings.fields.advanced.${fieldConfig.key}`,
                    fieldConfig.label,
                  )}
                  type="number"
                  min={fieldConfig.min}
                  max={fieldConfig.max}
                  required
                  value={pbx[fieldConfig.key]}
                  onChange={(value) => onPbxFieldChange(fieldConfig.key, value)}
                />
              ))}

              <SelectField
                t={t}
                label={text(t, 'pbxSettings.fields.industry.label', 'Industry')}
                required
                value={pbx.industry_type}
                onChange={(value) => onPbxFieldChange('industry_type', value)}
                options={INDUSTRY_OPTIONS}
                placeholder={text(
                  t,
                  'pbxSettings.placeholders.selectFromList',
                  'Select from the List...',
                )}
              />
              <SelectField
                t={t}
                label={text(t, 'pbxSettings.fields.ehr.label', 'EHR')}
                value={pbx.ehr_type}
                onChange={(value) => onPbxFieldChange('ehr_type', value)}
                options={ehrOptions}
                helperText={text(
                  t,
                  'pbxSettings.fields.ehr.helperText',
                  'For reporting purposes only, does not impact functionality',
                )}
              />
            </Box>
          </Card.Body>
        </Collapsible.Content>
      </Card.Root>
    </Collapsible.Root>
  )
}
